/**
 * The pre-invitation sharing disclosure (Feature: user-and-data-management; **Requirement 6**;
 * Requirement 5.6; §Security findings 2).
 *
 * The scenario Requirement 6 exists for: someone shares "a check" believing they are sharing a URL
 * and a schedule, and actually shares every response body and trace that check has captured. So this
 * is not a tooltip and not a link to documentation — 6.1 requires the consequences to be stated
 * *specifically and without requiring a documentation visit*, which means the prose is the feature.
 * {@link import('./InfoTooltip').InfoTooltip} is the right affordance for a short hint; this is the
 * wrong thing to hide behind an `i` button.
 *
 * Three structural decisions carry the requirement rather than merely describing it:
 *
 *  - **Acknowledgement is part of the component, not the form.** 6.6 asks for an explicit affirmative
 *    action *distinct from a single click on a link* — the disclosure must be acknowledged, not merely
 *    displayed. Passing the `acknowledgement` pair renders the checkbox inside the disclosure, so a
 *    caller cannot render the text without also rendering the thing that gates the send. (The pair is
 *    one optional object rather than two loose props, so "half-wired" is not expressible.)
 *  - **Role-conditional, but only in one direction.** 6.2 and 6.5 describe powers a viewer will not
 *    have, and showing someone warnings that do not apply to what they are doing trains them to skip
 *    the ones that do. So the write sections appear only for a write-capable role. The third-party
 *    data (6.3) and non-retroactive revocation (6.4) sections are **never** conditional: they are true
 *    of every role, including a viewer, and a viewer invitation is exactly the case where a sharer is
 *    most likely to assume there is nothing to think about.
 *  - **{@link WriteAccessWarning} is extracted** because the same escalation happens when an existing
 *    viewer is *promoted* to a write role, which is not an invitation and so is not literally
 *    Requirement 6. Sharing the component is what stops the two paths' descriptions of the same
 *    consequence from drifting apart.
 */

import type { MemberRole } from '../api/types';
import { roleCanWrite } from '../api/types';

/** Human labels for the role `<select>`, stating the privilege rather than just naming it. */
export const ROLE_LABELS: Record<MemberRole, string> = {
  viewer: 'Viewer — read only',
  editor: 'Editor — can create, change, and delete',
  admin: 'Admin — full control, including API keys and membership',
};

/**
 * What granting write access means (Req 6.2), and the consequence §Security findings 2 records
 * (Req 6.5).
 *
 * Finding 2 is the one that is genuinely counter-intuitive, and it is the reason 6.5 exists: the
 * platform masks secret *values* on read, which is a real protection against a reader and no
 * protection at all against a writer. Stating "secrets are masked" without stating this would be
 * worse than saying nothing, because it would leave the sharer with a false sense of a control that
 * does not cover the case they just created.
 */
export function WriteAccessWarning({ role }: { role: MemberRole }): JSX.Element | null {
  if (!roleCanWrite(role)) return null;

  return (
    <div data-testid="disclosure-write">
      <h4>They will also be able to change and destroy things</h4>
      <ul>
        <li>
          Create, modify, and delete this account&apos;s checks and alert rules — including deleting
          checks other members rely on.
        </li>
        {role === 'admin' ? (
          <li data-testid="disclosure-admin-keys">
            Mint API keys for this account. A key authenticates on its own, without their sign-in, and
            keeps working until it is revoked.
          </li>
        ) : null}
      </ul>

      <h4>Write access defeats secret masking</h4>
      <p data-testid="disclosure-secret-exfiltration">
        Secret values are masked whenever they are read, so a viewer cannot see them. That protection
        does not extend to someone who can write: they can repoint a check that references one of your
        secrets at a server they control, run it, and read the secret out of their own logs. Masking
        cannot prevent this. Granting write access is a decision to trust this person with every
        secret this account references.
      </p>
    </div>
  );
}

/** The controlled acknowledgement checkbox's state, passed as one object so it cannot be half-wired. */
export interface DisclosureAcknowledgement {
  checked: boolean;
  onChange: (checked: boolean) => void;
}

export interface SharingDisclosureProps {
  /** The role being granted. Decides whether the write-specific sections apply (Req 6.2, 6.5). */
  readonly role: MemberRole;
  /**
   * Renders the acknowledgement checkbox that gates the send (Req 6.6). Omit to show the disclosure
   * as information only — there is no path where the invite button is live without this.
   */
  readonly acknowledgement?: DisclosureAcknowledgement;
}

export function SharingDisclosure({ role, acknowledgement }: SharingDisclosureProps): JSX.Element {
  return (
    <div role="note" className="sharing-disclosure" data-testid="sharing-disclosure">
      <h3>What this invitation gives away</h3>

      <p>
        Sharing in Soroush is account-wide. There is no per-check sharing: this person joins the whole
        account and sees everything in it, not the one check you had in mind.
      </p>

      <div data-testid="disclosure-read">
        <h4>They will be able to read</h4>
        <ul>
          <li>
            Every check&apos;s configuration — its target URLs, headers, request bodies, assertions,
            and schedule.
          </li>
          <li>
            Every result, <strong>including the response bodies the platform captured</strong> from
            your monitored APIs.
          </li>
          <li>
            Every <strong>trace</strong>, including the per-step request and response detail behind
            each result.
          </li>
          <li>Every incident, and the notifications sent for it.</li>
          <li>
            Every generation request and check draft, including the{' '}
            <strong>source documents uploaded</strong> to produce them.
          </li>
        </ul>
      </div>

      <WriteAccessWarning role={role} />

      {/* 6.3 and 6.4 are true of every role, so neither is ever conditional. */}
      <div data-testid="disclosure-third-party">
        <h4>Results and traces may hold other people&apos;s personal data</h4>
        <p>
          Captured response bodies contain whatever your monitored APIs returned, which may be
          personal data about <strong>third parties</strong> — the people your own service&apos;s data
          is about, who are not party to this invitation. Showing it to this person is a disclosure
          you are making, and you are responsible for having a lawful basis for it.
        </p>
      </div>

      <div data-testid="disclosure-revocation">
        <h4>This cannot be undone retroactively</h4>
        <p>
          You can remove this member later. That stops their access from their next request onward. It
          does <strong>not</strong> reach anything they have already read, exported, or copied, and the
          platform has no way to retrieve it. Treat whatever they can see while they are a member as
          permanently theirs.
        </p>
      </div>

      {acknowledgement !== undefined ? (
        <label className="checkbox-row disclosure-acknowledgement">
          <input
            type="checkbox"
            checked={acknowledgement.checked}
            onChange={(event) => acknowledgement.onChange(event.target.checked)}
          />
          <span>
            I have read what this invitation gives away, and I am authorised to disclose this
            account&apos;s data — including captured response bodies — to this person.
          </span>
        </label>
      ) : null}
    </div>
  );
}
